import { LOCALES } from '@rythmo/shared';
import { type LucideIcon, Monitor, Moon, Sun } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { useI18n } from '../i18n/I18n.js';
import { applyTheme, readTheme, storeTheme, THEMES, type Theme } from '../theme/theme.js';

/** The Pulse mark and the lowercase wordmark, composed live (the wordmark is text, not art). */
export function Lockup({ size = 22 }: { size?: number }) {
  return (
    <span className="lockup">
      <img src="/rythmo-mark.svg" width={size} height={size} alt="" />
      <span className="lockup-word">rythmo</span>
    </span>
  );
}

/** Română · English, as two pressed-state buttons: the choice is remembered on this browser. */
export function LanguageSwitch() {
  const { locale, setLocale, t } = useI18n();
  const names = { ro: 'RO', en: 'EN' } as const;
  const full = { ro: 'Română', en: 'English' } as const;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not a form fieldset.
    <div className="switch language" role="group" aria-label={t.shell.language}>
      {LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          lang={code}
          aria-pressed={code === locale}
          aria-label={full[code]}
          onClick={() => setLocale(code)}
        >
          {names[code]}
        </button>
      ))}
    </div>
  );
}

const THEME_ICONS: Record<Theme, LucideIcon> = { system: Monitor, light: Sun, dark: Moon };

/** The system's theme, light or dark, as three pressed-state buttons, remembered on this browser. */
export function ThemeSwitch() {
  const { t } = useI18n();
  const [theme, setTheme] = useState<Theme>(readTheme);
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not a form fieldset.
    <div className="switch theme" role="group" aria-label={t.shell.theme}>
      {THEMES.map((choice) => {
        const Icon = THEME_ICONS[choice];
        return (
          <button
            key={choice}
            type="button"
            aria-pressed={choice === theme}
            aria-label={t.shell.themes[choice]}
            title={t.shell.themes[choice]}
            onClick={() => {
              applyTheme(choice);
              storeTheme(choice);
              setTheme(choice);
            }}
          >
            <Icon size={14} strokeWidth={1.75} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

/** The person's two preferences, side by side: the language and the theme. */
export function Preferences() {
  return (
    <div className="preferences">
      <LanguageSwitch />
      <ThemeSwitch />
    </div>
  );
}

/**
 * A Rythmo page for a state with no school yet: signed out, no access, a failed sign-in, the
 * picker. The heading says what happened, the line says what to do, one action does it.
 */
export function Notice({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="notice">
      <header className="notice-top">
        <Lockup />
        <Preferences />
      </header>
      <main className="notice-body">
        <h1 className="notice-title">{title}</h1>
        {children}
        {action}
      </main>
    </div>
  );
}
